import { useEffect, useState, useCallback, memo, lazy } from 'react';
import { useGetAppDisplayConfig, useUpdateAppDisplayConfig } from '../../queries/homepageOrganizer';
import { useForm } from 'react-hook-form';
import { ActionButton } from '../../components/Forms/ActionButton';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConfigField from '../../components/Forms/ConfigField';
import LazyMountSection from './LazyMountSection';

// Lazy load heavy components to improve initial load time
const AppHeroSection = lazy(() => import('./AppHeroSection'));
const DoYouKnowSection = lazy(() => import('./DoYouKnowSection'));
const MoreChallenges = lazy(() => import('./MoreChallenges'));
const BlogsSection = lazy(() => import('./BlogsSection'));
const EventsSection = lazy(() => import('./EventsSection'));
const PartnersSection = lazy(() => import('./PartnersSection'));
const EcoActivitySection = lazy(() => import('./EcoActivitySection'));
const InviteMessages = lazy(() => import('./InviteMessages'));

// Memoized config field groups
const ConfigFieldGroup = memo(({ children }) => (
    <div className='flex items-center justify-between gap-3 mt-2'>{children}</div>
));
ConfigFieldGroup.displayName = 'ConfigFieldGroup';

const ConfigCard = memo(({ children }) => (
    <div className="bg-white shadow-md rounded-lg p-5 basis-1/2 my-1">
        {children}
    </div>
));
ConfigCard.displayName = 'ConfigCard';

const HomepageOrganizer = () => {
    const { data, isPending } = useGetAppDisplayConfig();
    const { mutateAsync: updateAppDisplayConfig, isPending: isUpdatingAppDisplayConfig } = useUpdateAppDisplayConfig();
    const [showDisplayConfigs, setShowDisplayConfigs] = useState(false);

    const { register, handleSubmit, formState: { errors }, setValue } = useForm();

    // Memoized submit handler
    const handleUpdateAppDisplayConfig = useCallback(async (formData) => {
        const parseIntOrFallback = (value, fallback) => parseInt(value) || fallback;

        await updateAppDisplayConfig({
            id: data._id,
            allowResubmissionOfSameChallengeAfterHowManyDays: parseIntOrFallback(formData.allowResubmissionOfSameChallengeAfterHowManyDays, data.allowResubmissionOfSameChallengeAfterHowManyDays),
            inviteUserToAppPoints: parseIntOrFallback(formData.inviteUserToAppPoints, data.inviteUserToAppPoints),
            inviteUserToChallengePoints: parseIntOrFallback(formData.inviteUserToChallengePoints, data.inviteUserToChallengePoints),
            invitedUserToAppPoints: parseIntOrFallback(formData.invitedUserToAppPoints, data.invitedUserToAppPoints),
            invitedUserToChallengePoints: parseIntOrFallback(formData.invitedUserToChallengePoints, data.invitedUserToChallengePoints),
            proposedChallengeVotingDays: parseIntOrFallback(formData.proposedChallengeVotingDays, data.proposedChallengeVotingDays),
            suggestChallengePoints: parseIntOrFallback(formData.suggestChallengePoints, data.suggestChallengePoints),
            userBlogAutoApprove: formData.userBlogAutoApprove === 'true',
            userBlogSubmissionPoints: parseIntOrFallback(formData.userBlogSubmissionPoints, data.userBlogSubmissionPoints),
            inviteUserToEventPoints: parseIntOrFallback(formData.inviteUserToEventPoints, data.inviteUserToEventPoints),
            invitedUserToEventPoints: parseIntOrFallback(formData.invitedUserToEventPoints, data.invitedUserToEventPoints),
            carbonSavingPointsPerKg: parseIntOrFallback(formData.carbonSavingPointsPerKg, data.carbonSavingPointsPerKg),
            quizPoints: parseIntOrFallback(formData.quizPoints, data.quizPoints),
        }, {
            onSuccess: () => toast.success("App display configuration updated successfully"),
            onError: (error) => toast.error(`Error updating app display configuration: ${error.message}`)
        });
    }, [data, updateAppDisplayConfig]);

    // Memoized form initialization
    useEffect(() => {
        if (!data) return;

        const fields = {
            allowResubmissionOfSameChallengeAfterHowManyDays: data.allowResubmissionOfSameChallengeAfterHowManyDays || 0,
            inviteUserToAppPoints: data.inviteUserToAppPoints || 0,
            inviteUserToChallengePoints: data.inviteUserToChallengePoints || 0,
            invitedUserToAppPoints: data.invitedUserToAppPoints || 0,
            invitedUserToChallengePoints: data.invitedUserToChallengePoints || 0,
            proposedChallengeVotingDays: data.proposedChallengeVotingDays || 0,
            suggestChallengePoints: data.suggestChallengePoints || 0,
            userBlogAutoApprove: data.userBlogAutoApprove,
            userBlogSubmissionPoints: data.userBlogSubmissionPoints || 0,
            inviteUserToEventPoints: data.inviteUserToEventPoints || 0,
            invitedUserToEventPoints: data.invitedUserToEventPoints || 0,
            carbonSavingPointsPerKg: data.carbonSavingPointsPerKg || 0,
            quizPoints: data.quizPoints || 0,
        };

        Object.entries(fields).forEach(([key, value]) => setValue(key, value));
    }, [data, setValue]);

    const toggleShow = useCallback(() => setShowDisplayConfigs(prev => !prev), []);

    if (isPending || isUpdatingAppDisplayConfig) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="loader">Loading...</div>
            </div>
        );
    }

    return (
        <>
            <div className='bg-white shadow-md rounded-lg p-5 flex items-center gap-2'>
                <h2 className="text-2xl text-primary font-bold cursor-pointer" onClick={toggleShow}>
                    App Display Configurations
                </h2>
                <p className='text-base text-black font-bold cursor-pointer' onClick={toggleShow}>
                    {showDisplayConfigs ? "Hide" : "Show"}
                </p>
            </div>

            {showDisplayConfigs && (
                <form onSubmit={handleSubmit(handleUpdateAppDisplayConfig)}>
                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Carbon Saving Points Per KG:"
                                name="carbonSavingPointsPerKg"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="Allow resubmission of the challenge after how many days:"
                                name="allowResubmissionOfSameChallengeAfterHowManyDays"
                                placeholder="Resubmission days"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Invite User to App Points(Referred User):"
                                name="inviteUserToAppPoints"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="Invited User to App Points(New User):"
                                name="invitedUserToAppPoints"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Invite User to Challenge Points(Person who invites):"
                                name="inviteUserToChallengePoints"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="Invited User to Challenge Points(Person who is invited):"
                                name="invitedUserToChallengePoints"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Proposed Challenge Voting Days:"
                                name="proposedChallengeVotingDays"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="Proposed Challenge Points:"
                                name="suggestChallengePoints"
                                placeholder="Proposed Challenge Points"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="User Submitted Blog Auto Approve:"
                                name="userBlogAutoApprove"
                                type="select"
                                options={[
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ]}
                                register={register}
                                errors={errors}
                                setValue={setValue}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="User Submitted Blog Points:"
                                name="userBlogSubmissionPoints"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Invite to Event(Person who invites):"
                                name="inviteUserToEventPoints"
                                placeholder="Invite to Event Points"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <ConfigCard>
                            <ConfigField
                                label="Invite to Event(Person who is invited):"
                                name="invitedUserToEventPoints"
                                placeholder="Invite to Event Points"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                    </ConfigFieldGroup>

                    <ConfigFieldGroup>
                        <ConfigCard>
                            <ConfigField
                                label="Default Quiz points:"
                                name="quizPoints"
                                placeholder="Quiz Points"
                                register={register}
                                errors={errors}
                            />
                        </ConfigCard>
                        <div className="basis-1/2"></div>
                    </ConfigFieldGroup>

                    <div className='flex justify-end mt-5'>
                        <ActionButton
                            text={isUpdatingAppDisplayConfig ? 'Updating...' : 'Update All'}
                            variant={'submit'}
                            disabled={isUpdatingAppDisplayConfig}
                        />
                    </div>
                </form>
            )}

            <LazyMountSection title="App Invite Message Display Configurations">
                <InviteMessages data={data} hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Home Page Hero Section">
                <AppHeroSection hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Do You Know Section">
                <DoYouKnowSection hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="More Challenges Section">
                <MoreChallenges hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Blogs Section">
                <BlogsSection hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Events Section">
                <EventsSection hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Partners Section">
                <PartnersSection hideHeader={true} />
            </LazyMountSection>

            <LazyMountSection title="Eco Activity Section">
                <EcoActivitySection hideHeader={true} />
            </LazyMountSection>

            <ToastContainer position="top-right" />
        </>
    );
}

export default HomepageOrganizer;
